import type { Surroundings } from '../../models/Surroundings';
import { landThresholds as thresholds, AI, SOCIAL, Direction, Directions, DirectionsVectors } from '../../models/constants';

/**
 * Returns the Direction whose unit vector most closely aligns with the vector
 * from `from` to `to`. Used by states that need to move toward a target position.
 */
export function directionToward(from: { x: number; z: number }, to: { x: number; z: number }): Direction {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    let best = Directions[0];
    let bestDot = -Infinity;
    for (const dir of Directions) {
        const vec = DirectionsVectors.get(dir)!;
        const dot = vec.dx * dx + vec.dz * dz;
        if (dot > bestDot) {
            bestDot = dot;
            best = dir;
        }
    }
    return best;
}

/** Minimal interface required by bestDirection and scoreDirection. */
type Mover = { x: number; z: number; moveDirection: Direction | null };

/**
 * Chooses the best direction to move in.
 *
 * @param being - The entity moving (needs position and current heading).
 * @param surroundings - Local terrain and neighbour snapshot.
 * @param inDanger - When true, danger penalties are weighted more heavily.
 * @param socialWeight - Strength of the bias toward nearby beings. 0 (default)
 *   disables social steering entirely (Level 1 beings, exploring beasts). A
 *   positive value biases directions toward the centroid of `nearbyBeings`
 *   (beings clustering) and applies a comfort ramp: the pull fades to zero once a
 *   neighbour is within `SOCIAL.COMFORT_RADIUS_SQ`, so beings stay loosely near the
 *   group instead of packing tight. A negative value biases away from it at full
 *   strength (a fleeing beast avoiding the herd) with no comfort ramp — the beast
 *   wants maximum distance, not a comfortable one. Unlike the terrain scoring, this
 *   term applies whether or not the mover is in danger, so a beast still steers
 *   clear of beings as it flees.
 */
export function bestDirection(being: Mover, surroundings: Surroundings, inDanger: boolean, socialWeight = 0): Direction {
    let bestDir = Directions[0];
    let bestScore = -Infinity;

    const pull = (socialWeight !== 0) ? socialPull(being, surroundings, socialWeight > 0) : null;

    for (const dir of Directions) {
        let score = scoreDirection(dir, being, surroundings, inDanger);

        if (pull) {
            const vec = DirectionsVectors.get(dir)!;
            const len = Math.hypot(vec.dx, vec.dz) || 1;
            const alignment = (vec.dx * pull.x + vec.dz * pull.z) / len;
            score += socialWeight * alignment;
        }

        if (score > bestScore) {
            bestScore = score;
            bestDir = dir;
        }
    }
    return bestDir;
}

/**
 * Returns a vector pointing from `mover` toward the centroid of nearby beings, or
 * null when there are none (or the mover already sits on the centroid).
 *
 * @param applyComfort - When true (attraction), the vector's magnitude ramps
 *   linearly from 0 at `SOCIAL.COMFORT_RADIUS_SQ` up to 1 at `SOCIAL.SENSE_RADIUS_SQ`,
 *   keyed on the nearest neighbour's distance — so a being whose closest neighbour is
 *   already within comfort range feels no pull and won't crowd in. When false
 *   (avoidance), the returned vector is unit length: a fleeing beast steers away at
 *   full strength regardless of how close the herd is.
 */
function socialPull(
    mover: Mover,
    surroundings: Surroundings,
    applyComfort: boolean
): { x: number; z: number } | null {
    const beings = surroundings.nearbyBeings;
    if (beings.length === 0) return null;

    let cx = 0;
    let cz = 0;
    let nearestSq = Infinity;
    for (const b of beings) {
        cx += b.x;
        cz += b.z;
        const ndx = b.x - mover.x;
        const ndz = b.z - mover.z;
        const dSq = ndx * ndx + ndz * ndz;
        if (dSq < nearestSq) nearestSq = dSq;
    }
    cx /= beings.length;
    cz /= beings.length;

    const dx = cx - mover.x;
    const dz = cz - mover.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-6) return null;

    let magnitude = 1;
    if (applyComfort) {
        const nearest = Math.sqrt(nearestSq);
        const comfort = Math.sqrt(SOCIAL.COMFORT_RADIUS_SQ);
        const sense = Math.sqrt(SOCIAL.SENSE_RADIUS_SQ);
        magnitude = Math.max(0, Math.min(1, (nearest - comfort) / (sense - comfort)));
        if (magnitude <= 0) return null;
    }

    return { x: (dx / len) * magnitude, z: (dz / len) * magnitude };
}

function scoreDirection(
    dir: Direction,
    being: Mover,
    surroundings: Surroundings,
    inDanger: boolean
): number {
    const line = surroundings.sightLines.get(dir) ?? [];
    let score = 0;

    for (let i = 0; i < line.length; i++) {
        const alt = line[i];
        const weight = 1 / (i + 1);

        if (alt === null) {
            score += -6.0 * weight;
            break;
        } else if (alt < thresholds.sea) {
            score += (inDanger ? -5.0 : -3.0) * weight;
        } else if (alt > thresholds.mountain) {
            score += (inDanger ? -4.0 : -2.0) * weight;
        } else if (alt < thresholds.highland) {
            score += (inDanger ? 0.5 : 1.0) * weight;
        }
    }

    if (surroundings.edgeDist < AI.INLAND_BIAS_THRESHOLD) {
        score += (surroundings.edgeDelta.get(dir) ?? 0) * 0.2;
    }

    if (dir === being.moveDirection) {
        score += 0.3;
    }

    return score;
}
